import { Fragment, useCallback, useEffect, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { parseInstant } from "../../utils/dateUtils";
import { adminService } from "../../features/admin/service";
import { useAdminToast } from "../../context/AdminToastContext";
import Pagination from "../../components/admin/Pagination";
import Spinner from "../../components/ui/Spinner";
import RefreshButton from "../../components/admin/RefreshButton";
import { emailTypeLabel, otpPurposeLabel } from "../../features/admin/logTypeLabels";

const sectionSpinner = (
  <div className="flex items-center justify-center py-10 text-gray-500 text-sm"><Spinner /></div>
);

type EmailLog = {
  id: string;
  call_type: string;
  to: string;
  subject: string;
  status: string;
  error: string | null;
  campaign_id: string | null;
  lead_id: string | null;
  purpose: string | null;
  backend: string | null;
  endpoint: string | null;
  request_payload: Record<string, unknown> | null;
  response_body: unknown;
  created_at: string | null;
};

type StatusFilter = "" | "completed" | "failed" | "skipped";
const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: "", label: "All" },
  { key: "completed", label: "Completed" },
  { key: "failed", label: "Failed" },
  { key: "skipped", label: "Skipped" },
];

const STATUS_STYLES: Record<string, string> = {
  completed: "bg-emerald-500/10 text-emerald-400",
  failed: "bg-red-500/10 text-red-400",
  skipped: "bg-amber-500/10 text-amber-400",
};

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  const d = parseInstant(iso) ?? new Date(iso);
  return d.toLocaleString();
}

const AdminEmailPage: React.FC = () => {
  const { showError } = useAdminToast();
  const [summary, setSummary] = useState<{ completed: number; failed: number; skipped: number; total: number } | null>(null);
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [logsError, setLogsError] = useState("");
  useEffect(() => { if (logsError) showError(logsError); }, [logsError, showError]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");
  const [typeFilter, setTypeFilter] = useState("");
  const [types, setTypes] = useState<{ call_type: string; count: number }[]>([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState(8);

  const [backendStatus, setBackendStatus] = useState<{ backend: string; configured: boolean } | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);

  const checkStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      setBackendStatus(await adminService.getEmailBackendStatus());
    } catch (err) {
      console.error(err);
      setBackendStatus({ backend: "unknown", configured: false });
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => { void checkStatus(); }, [checkStatus]);

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    setLogsError("");
    try {
      const [logsRes, summaryRes, typesRes] = await Promise.all([
        adminService.getEmailLogs({
          status: statusFilter || undefined,
          call_type: typeFilter || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          limit: pageSize,
          skip: page * pageSize,
        }),
        adminService.getEmailLogsSummary(),
        adminService.getEmailLogTypes(),
      ]);
      setLogs(logsRes.items);
      setTotal(logsRes.total);
      setSummary(summaryRes);
      setTypes(typesRes.items);
    } catch (err) {
      console.error(err);
      setLogsError("Failed to load email call logs");
    } finally {
      setLogsLoading(false);
    }
  }, [statusFilter, typeFilter, dateFrom, dateTo, page, pageSize]);

  useEffect(() => { void loadLogs(); }, [loadLogs]);
  useEffect(() => { setPage(0); }, [pageSize]);

  const handleRefreshAll = useCallback(() => {
    return Promise.all([checkStatus(), loadLogs()]).then(() => {});
  }, [checkStatus, loadLogs]);

  return (
    <div className="p-6 max-w-5xl">
      <AdminPageHeader
        title="Email Call Log"
        subtitle="Monitor every outbound transactional email (enquiry alerts, verification, etc)."
        actions={<RefreshButton onRefresh={handleRefreshAll} />}
      />

      {/* Email backend status */}
      <div className="mb-8 flex items-center gap-3 bg-gray-900 border border-gray-800 rounded-xl px-4 py-3 max-w-2xl">
        <span
          className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${
            statusLoading ? "bg-gray-500 animate-pulse" : backendStatus?.configured ? "bg-emerald-500" : "bg-red-500"
          }`}
        />
        <div className="flex-1">
          <p className="text-sm text-gray-300">
            Email backend: {statusLoading ? "checking…" : backendStatus?.configured ? `configured (${backendStatus.backend})` : "not configured"}
          </p>
        </div>
        <button
          onClick={checkStatus}
          disabled={statusLoading}
          className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-700 text-gray-300 hover:text-white disabled:opacity-50"
        >
          Recheck
        </button>
      </div>

      {/* Call logs */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
          <h2 className="text-lg font-semibold text-white">Email Call Log</h2>
          <div className="flex flex-wrap items-center gap-2">
            <select
              aria-label="Filter by type"
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
              className="px-2 py-1.5 rounded-lg text-xs font-medium bg-gray-900 border border-gray-800 text-gray-300"
            >
              <option value="">All types</option>
              {types.map((t) => (
                <option key={t.call_type} value={t.call_type}>
                  {emailTypeLabel(t.call_type)} · {t.call_type} ({t.count})
                </option>
              ))}
            </select>
            <div className="flex items-center gap-1">
              <input
                type="date"
                aria-label="From date"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
                className="px-2 py-1.5 rounded-lg text-xs font-medium bg-gray-900 border border-gray-800 text-gray-300"
              />
              <span className="text-gray-600 text-xs">to</span>
              <input
                type="date"
                aria-label="To date"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
                className="px-2 py-1.5 rounded-lg text-xs font-medium bg-gray-900 border border-gray-800 text-gray-300"
              />
              {(dateFrom || dateTo) && (
                <button
                  onClick={() => { setDateFrom(""); setDateTo(""); setPage(0); }}
                  className="px-2 py-1.5 rounded-lg text-xs font-medium text-gray-400 hover:text-white"
                  title="Clear date range"
                >
                  ✕
                </button>
              )}
            </div>
            {STATUS_FILTERS.map(({ key, label }) => (
              <button
                key={key || "all"}
                onClick={() => { setStatusFilter(key); setPage(0); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
                  statusFilter === key
                    ? "bg-indigo-600 border-indigo-600 text-white"
                    : "bg-gray-900 border-gray-800 text-gray-400 hover:text-white"
                }`}
              >
                {label}
                {summary && key !== "" && ` (${summary[key]})`}
              </button>
            ))}
          </div>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Every outbound email send, including OTP codes (type <code>otp_email</code>) — endpoint, payload and provider
          response. OTP codes are masked; the HTML body is omitted.
        </p>

        {logsError && <p className="text-sm text-red-400 mb-3">{logsError}</p>}

        {logsLoading ? sectionSpinner : (
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800 text-left text-gray-500 text-xs uppercase">
                  <th className="px-4 py-3">Time</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">To</th>
                  <th className="px-4 py-3">Subject</th>
                  <th className="px-4 py-3">Campaign</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">No calls logged yet.</td>
                  </tr>
                )}
                {logs.map((log) => (
                  <Fragment key={log.id}>
                    <tr
                      onClick={() => setExpanded(expanded === log.id ? null : log.id)}
                      className="border-b border-gray-800 last:border-0 cursor-pointer hover:bg-gray-800/50"
                    >
                      <td className="px-4 py-3 text-gray-400 whitespace-nowrap">{fmtDate(log.created_at)}</td>
                      <td className="px-4 py-3 text-gray-300">
                        {emailTypeLabel(log.call_type)}
                        {log.purpose && (
                          <span className="block text-xs text-gray-400">for {otpPurposeLabel(log.purpose)}</span>
                        )}
                        <span className="block text-xs text-gray-500 font-mono">{log.call_type}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[log.status] ?? "bg-gray-700 text-gray-300"}`}>
                          {log.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-400">{log.to || "—"}</td>
                      <td className="px-4 py-3 text-gray-400 max-w-xs truncate">{log.subject || "—"}</td>
                      <td className="px-4 py-3 text-gray-400">{log.campaign_id ?? "—"}</td>
                      <td className="px-4 py-3 text-gray-500">{expanded === log.id ? "▲" : "▼"}</td>
                    </tr>
                    {expanded === log.id && (
                      <tr className="border-b border-gray-800 last:border-0 bg-gray-950">
                        <td colSpan={7} className="px-4 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Endpoint</p>
                              <p className="text-xs text-gray-300 break-all mb-3">
                                {log.endpoint ?? "—"}
                                {log.backend && <span className="text-gray-500"> ({log.backend})</span>}
                              </p>
                              {log.purpose && (
                                <>
                                  <p className="text-xs text-gray-500 mb-1">Purpose</p>
                                  <p className="text-xs text-gray-300 mb-3">{otpPurposeLabel(log.purpose)}</p>
                                </>
                              )}
                              {log.lead_id && (
                                <>
                                  <p className="text-xs text-gray-500 mb-1">Lead</p>
                                  <p className="text-xs text-gray-300 break-all mb-3">{log.lead_id}</p>
                                </>
                              )}
                              <p className="text-xs text-gray-500 mb-1">Data sent</p>
                              <pre className="text-xs text-gray-300 bg-gray-900 border border-gray-800 rounded-lg p-3 overflow-x-auto">
                                {JSON.stringify(log.request_payload ?? { to: log.to, subject: log.subject }, null, 2)}
                              </pre>
                            </div>
                            <div>
                              {log.error && (
                                <>
                                  <p className="text-xs text-red-400 mb-1">Error</p>
                                  <p className="text-xs text-red-300 mb-3 break-all">{log.error}</p>
                                </>
                              )}
                              <p className="text-xs text-gray-500 mb-1">Response</p>
                              <pre className="text-xs text-gray-300 bg-gray-900 border border-gray-800 rounded-lg p-3 overflow-x-auto">
                                {JSON.stringify(log.response_body ?? {}, null, 2)}
                              </pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
            </div>
            <div className="border-t border-gray-800">
              <Pagination
                variant="panel"
                page={page}
                pageSize={pageSize}
                total={total}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEmailPage;
